const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://127.0.0.1:8787';

export type FinalEvaluationPeriod = 'first_half' | 'second_half';

export type FinalEvaluationResponse = {
  exists: boolean;
  isFinalized: boolean;
  finalizedAt: string | null;
  rank: string | null;
  aiSummary: string;
  aiStrength: string;
  aiImprovement: string;
  performanceScore: number;
  behaviorScore: number;
  adjustmentScore: number;
  executiveAdjustmentScore: number;
  totalScore: number;
};

export type FinalEvaluationSubmission = {
  submissionKey: string;
  userId: string;
  userName: string;
  department: string;
  role: string;
  termNo: number;
  period: 'first_half' | 'second_half';
  isFinalized: boolean;
  performanceScore: number;
  behaviorScore: number;
  adjustmentScore: number;
  executiveAdjustmentScore: number;
  totalScore: number;
  rank: string | null;
  submittedAt: string;
};

async function throwIfNotOk(response: Response) {
  if (response.ok) return;
  const payload = await response.json().catch(() => ({}));
  const message = typeof payload.message === 'string' ? payload.message : 'API request failed';
  throw new Error(message);
}

export async function fetchFinalEvaluation(input: {
  userId: string;
  termNo: number;
  period: FinalEvaluationPeriod;
}) {
  const params = new URLSearchParams({
    userId: input.userId,
    termNo: String(input.termNo),
    period: input.period,
  });
  const response = await fetch(`${apiBaseUrl}/api/final-evaluations?${params}`);
  await throwIfNotOk(response);
  return (await response.json()) as FinalEvaluationResponse;
}

export async function saveFinalEvaluation(input: {
  userId: string;
  termNo: number;
  period: FinalEvaluationPeriod;
  isFinalized: boolean;
  userName: string;
  department: string;
  role: string;
  performanceScore: number;
  behaviorScore: number;
  adjustmentScore: number;
  executiveAdjustmentScore: number;
  totalScore: number;
  rank?: string | null;
}) {
  const response = await fetch(`${apiBaseUrl}/api/final-evaluations`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  await throwIfNotOk(response);
  return (await response.json()) as { saved: FinalEvaluationResponse };
}

export async function fetchFinalEvaluationSubmissions(input?: {
  onlyFinalized?: boolean;
}) {
  const params = new URLSearchParams();
  if (typeof input?.onlyFinalized === 'boolean') {
    params.set('onlyFinalized', String(input.onlyFinalized));
  }
  const suffix = params.toString() ? `?${params}` : '';
  const response = await fetch(`${apiBaseUrl}/api/final-evaluations/submissions${suffix}`);
  await throwIfNotOk(response);
  return (await response.json()) as { rows: FinalEvaluationSubmission[] };
}

export async function fetchFinalEvaluationMissingRequirements(input: {
  userId: string;
  termNo: number;
  period: FinalEvaluationPeriod;
}) {
  const params = new URLSearchParams({
    userId: input.userId,
    termNo: String(input.termNo),
    period: input.period,
  });
  const response = await fetch(
    `${apiBaseUrl}/api/final-evaluations/missing-requirements?${params.toString()}`,
  );
  await throwIfNotOk(response);
  return (await response.json()) as { items: string[] };
}

export async function fetchFinalEvaluationMissingRequirementsBatch(input: {
  userIds: string[];
  termNo: number;
  period: FinalEvaluationPeriod;
}) {
  const response = await fetch(
    `${apiBaseUrl}/api/final-evaluations/missing-requirements/batch`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    },
  );
  await throwIfNotOk(response);
  return (await response.json()) as { rows: Array<{ userId: string; items: string[] }> };
}

export async function generateFinalEvaluationAiComment(input: {
  userId: string;
  termNo: number;
  period: FinalEvaluationPeriod;
  userName?: string;
  performanceScore?: number;
  behaviorScore?: number;
  adjustmentScore?: number;
  executiveAdjustmentScore?: number;
  totalScore?: number;
}) {
  const response = await fetch(`${apiBaseUrl}/api/final-evaluations/ai-comment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  await throwIfNotOk(response);
  return (await response.json()) as {
    result: { summary: string; strength: string; improvement: string };
  };
}

export async function saveFinalEvaluationAiComment(input: {
  userId: string;
  termNo: number;
  period: FinalEvaluationPeriod;
  summary: string;
  strength: string;
  improvement: string;
}) {
  const response = await fetch(`${apiBaseUrl}/api/final-evaluations/ai-comment`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(input),
  });
  await throwIfNotOk(response);
  return (await response.json()) as {
    saved: { summary: string; strength: string; improvement: string };
  };
}
